import React, { useEffect, useState, useMemo } from 'react';
import {
  Collapse,
  Input,
  Modal,
  List,
  Typography,
  Avatar,
  Skeleton,
  Empty,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  FolderAddOutlined,
  UserOutlined,
  ExpandAltOutlined,
} from '@ant-design/icons';
import { Notify } from '../../services/notificationService';
import {
  getPromptLibrary,
  createPromptGroup,
  createPrompt,
  updatePrompt,
  deletePrompt,
  deletePromptGroup,
} from '../../services/promptLibraryService';
import { Prompt, PromptGroup } from '../../interfaces/promptLibrary';
import './PromptLibrary.less';
import { useAuth0 } from '@auth0/auth0-react';
import { ReactComponent as AppLogo } from '../../assets/logo-cloud-32.svg';
import { ReactComponent as CopyPlus } from '../../assets/copy-plus.svg';
import { confirmAction } from '../../utils/confirmActionModal';
import { useSelector } from 'react-redux';
import { useAgentSelector, useAgentDispatch } from '../../hooks/useAgentStore';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../constants/route';
import { setPrompts } from '../../app/Slices/promptLibrarySlice';
import ActionButton from '../ActionButton/ActionButton';
import { getReadonlyElementTitle } from '../../services/subscription';
import { selectIsSubscriptionReadonly } from '../../app/Slices/subscriptionSlice';
import { AxiosError } from 'axios';
import PromptLibraryFilter, { PromptFilterType } from './PromptLibraryFilter';
import { PROMPT_COMING_SOON } from '../../constants/texts';
const { Search } = Input;
const { Text } = Typography;

const getInitials = (name: string): string => {
  if (!name) return '';
  // Handle email addresses
  if (name.includes('@')) {
    return name.split('@')[0].slice(0, 2).toUpperCase();
  }
  // Handle regular names
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Generate a pastel color
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 80%)`;
};

const UserAvatar: React.FC<{
  userId: string;
  size?: number;
  isSystem?: boolean;
  userPicture?: string;
  userName?: string;
  isGroup?: boolean;
}> = ({ userId, size, isSystem, userPicture, userName, isGroup }) => {
  const getUserAvatar = useMemo(() => {
    const userColors = new Map<string, string>();
    const userInitials = new Map<string, string>();

    return (userId: string, userName?: string) => {
      if (!userColors.has(userId)) {
        userColors.set(userId, stringToColor(userId));
      }
      if (!userInitials.has(userId)) {
        userInitials.set(userId, getInitials(userName || userId));
      }

      return {
        color: userColors.get(userId) ?? stringToColor(userId),
        initials: userInitials.get(userId) ?? getInitials(userName || userId),
      };
    };
  }, []);

  const getTooltipTitle = () => {
    const itemType = isGroup ? 'group' : 'prompt';
    if (isSystem) return `System ${itemType}`;
    if (userPicture) return `${userName} (My ${itemType})`;
    return `${userId} (Team member ${itemType})`;
  };

  const getAvatarContent = () => {
    if (isSystem) {
      return <AppLogo />;
    }
    if (userPicture) {
      return <Avatar size={size} className="avatar" src={userPicture} />;
    }
    const { color, initials } = getUserAvatar(userId, userName);
    return (
      <Avatar
        size={size}
        className="avatar"
        style={{ backgroundColor: color }}
        icon={!initials ? <UserOutlined /> : undefined}
      >
        {initials}
      </Avatar>
    );
  };

  return <Tooltip title={getTooltipTitle()}>{getAvatarContent()}</Tooltip>;
};

const PromptLibrary: React.FC = () => {
  const [promptGroups, setPromptGroups] = useState<PromptGroup[]>([]);
  const [filterOptions, setFilterOptions] = useState<string[]>([
    PromptFilterType.SYSTEM,
    PromptFilterType.MY,
    PromptFilterType.TEAM,
  ]);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{
    open: boolean;
    groupId: string;
    prompt: Prompt | null;
    mode: 'add' | 'edit' | 'clone';
    loading: boolean;
  }>({
    open: false,
    groupId: '',
    prompt: null,
    mode: 'add',
    loading: false,
  });
  const [groupModal, setGroupModal] = useState({ open: false, loading: false });
  const [newGroup, setNewGroup] = useState({ label: '' });
  const [newPrompt, setNewPrompt] = useState({ label: '', value: '' });
  const { user } = useAuth0();
  const agentDispatch = useAgentDispatch();
  const { prompts, loading: promptLibraryLoading } = useAgentSelector(
    (state) => state.promptLibrary
  );
  const navigate = useNavigate();
  const isReadonly = useSelector(selectIsSubscriptionReadonly);
  const [activeKeys, setActiveKeys] = useState<string[]>(['0']);

  useEffect(() => {
    setPromptGroups(prompts);
    setLoading(promptLibraryLoading);
  }, [prompts, promptLibraryLoading]);

  const handleSearch = (value: string) => {
    setTimeout(() => {
      setDebouncedSearch(value);
    }, 300); // 300ms buffer
  };

  // Filtered prompt groups based on debounced search and filter options
  const filteredPromptGroups = useMemo(() => {
    let filtered = promptGroups;

    // Apply category filters
    if (filterOptions.length < 3) {
      // Only filter if not all options are selected
      filtered = promptGroups
        .map((group: PromptGroup) => {
          const filteredChildren = (group.children || []).filter(
            (prompt: Prompt) => {
              if (
                filterOptions.includes(PromptFilterType.SYSTEM) &&
                prompt.isSystem
              ) {
                return true;
              }
              if (
                filterOptions.includes(PromptFilterType.MY) &&
                prompt.cirraUserId === user?.email
              ) {
                return true;
              }
              if (
                filterOptions.includes(PromptFilterType.TEAM) &&
                !prompt.isSystem &&
                prompt.cirraUserId !== user?.email
              ) {
                return true;
              }
              return false;
            }
          );

          // Show group if it has matching children OR if the group itself matches the criteria
          if (
            filteredChildren.length > 0 ||
            (filterOptions.includes(PromptFilterType.SYSTEM) &&
              group.isSystem) ||
            (filterOptions.includes(PromptFilterType.MY) &&
              group.cirraUserId === user?.email) ||
            (filterOptions.includes(PromptFilterType.TEAM) &&
              !group.isSystem &&
              group.cirraUserId !== user?.email)
          ) {
            return { ...group, children: filteredChildren };
          }
          return null;
        })
        .filter((g): g is PromptGroup => g !== null);
    }

    // Apply search filter
    if (debouncedSearch.trim()) {
      const term = debouncedSearch.trim().toLowerCase();
      filtered = filtered
        .map((group: PromptGroup) => {
          const groupMatch = group.label.toLowerCase().includes(term);
          const filteredChildren = (group.children || []).filter(
            (prompt: Prompt) =>
              prompt.label.toLowerCase().includes(term) ||
              prompt.value.toLowerCase().includes(term)
          );
          if (groupMatch || filteredChildren.length > 0) {
            return {
              ...group,
              children: groupMatch ? group.children : filteredChildren,
            };
          }
          return null;
        })
        .filter((g): g is PromptGroup => g !== null);
    }

    return filtered;
  }, [debouncedSearch, promptGroups, filterOptions, user?.sub]);

  // Update activeKeys when search is applied
  useEffect(() => {
    if (debouncedSearch.trim()) {
      setActiveKeys(filteredPromptGroups.map((_, index) => index.toString()));
    } else {
      setActiveKeys(['0']);
    }
  }, [debouncedSearch, filteredPromptGroups]);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const data = await getPromptLibrary(filterOptions);
      agentDispatch(setPrompts(data));
    } catch (error) {
      Notify.error('Failed to fetch prompts library');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrompt = (groupId: string) => {
    setModal({
      open: true,
      groupId,
      prompt: null,
      mode: 'add',
      loading: false,
    });
    setNewPrompt({ label: '', value: '' });
  };

  const handleEditPrompt = (groupId: string, prompt: Prompt) => {
    if (prompt.isSystem) {
      Notify.warning('System prompts cannot be edited. Please clone it first.');
      return;
    }
    setModal({ open: true, groupId, prompt, mode: 'edit', loading: false });
    setNewPrompt({ label: prompt.label, value: prompt.value });
  };

  const handleUsePrompt = (groupId: string, prompt: Prompt) => {
    const encodedPrompt = encodeURIComponent(prompt.value);
    navigate(`${AppRoutes.adminAgent.path}?prompt=${encodedPrompt}`);
  };

  const handleClonePrompt = (groupId: string, prompt: Prompt) => {
    setModal({ open: true, groupId, prompt, mode: 'clone', loading: false });
    setNewPrompt({
      label: `${prompt.label} (Copy)`,
      value: prompt.value,
    });
  };

  const handleCopyPrompt = (groupId: string, prompt: Prompt) => {
    navigator.clipboard.writeText(prompt.value);
    Notify.success('Copied to clipboard');
  };

  const handleDeletePromptGroup = async (group: PromptGroup) => {
    if (group.isSystem) {
      Notify.warning('System prompts cannot be deleted.');
      return;
    }
    const confirmed = await confirmAction({
      title: 'Delete Prompt Group',
      content: `Are you sure you want to delete "${group.label}" prompt group?`,
      okText: 'Delete',
      okType: 'danger',
    });
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      await deletePromptGroup(group.id);
      await fetchPrompts();
      Notify.success('Prompt group deleted');
    } catch (error) {
      Notify.error('Failed to delete prompt group');
    }
  };

  const handleDeletePrompt = async (prompt: Prompt) => {
    if (prompt.isSystem) {
      Notify.warning('System prompts cannot be deleted.');
      return;
    }
    const confirmed = await confirmAction({
      title: 'Delete Prompt',
      content: `Are you sure you want to delete "${prompt.label}" prompt?`,
      okText: 'Delete',
      okType: 'danger',
    });
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      await deletePrompt(prompt.id);
      await fetchPrompts();
      Notify.success('Prompt deleted');
    } catch (error) {
      Notify.error('Failed to delete prompt');
    }
  };

  const handleModalOk = async () => {
    const label = newPrompt.label.trim();
    const value = newPrompt.value.trim();
    if (!label || !value) {
      Notify.warning('Label and value are required');
      return;
    }

    // Check if prompt label already exists in any group
    const promptExists = promptGroups.some((group) =>
      group.children.some((p) =>
        // Skip checking the current prompt's label when in edit mode
        modal.mode === 'edit' && modal.prompt?.id === p.id
          ? false
          : p.label === label
      )
    );
    if (promptExists) {
      Notify.warning('A prompt with this label already exists');
      return;
    }

    try {
      setModal({ ...modal, loading: true });
      if (modal.mode === 'edit' && modal.prompt) {
        await updatePrompt(modal.prompt.id, {
          label,
          value,
        });
      } else {
        await createPrompt({
          label,
          value,
          promptGroupId: modal.groupId,
        });
      }
      await fetchPrompts();
      setNewPrompt({ label: '', value: '' });
      Notify.success(modal.mode === 'edit' ? 'Prompt updated' : 'Prompt added');
    } catch (error) {
      Notify.error(
        ((error as AxiosError).response?.data as { message?: string })
          ?.message ||
          `Failed to ${modal.mode === 'edit' ? 'update' : 'add'} prompt`
      );
    } finally {
      setModal({
        open: false,
        groupId: '',
        prompt: null,
        mode: 'add',
        loading: false,
      });
    }
  };

  const handleAddGroup = async () => {
    const label = newGroup.label.trim();
    if (!label) {
      Notify.warning('Group label is required');
      return;
    }

    // Check if group key already exists
    if (promptGroups.some((g) => g.label === label)) {
      Notify.warning('A group with this label already exists');
      return;
    }

    try {
      setGroupModal({ ...groupModal, loading: true });
      await createPromptGroup({
        label,
      });
      await fetchPrompts();
      setNewGroup({ label: '' });
      Notify.success('Group added');
    } catch (error) {
      Notify.error(
        ((error as AxiosError).response?.data as { message?: string })
          ?.message || 'Failed to add group'
      );
    } finally {
      setGroupModal({ open: false, loading: false });
    }
  };

  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm.trim()) return text;
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <mark key={i} style={{ backgroundColor: '#ffd666', padding: 0 }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div id="prompt-library-container">
      <div className="prompt-library-wrapper">
        <div className="prompt-library-header">
          <h2 style={{ margin: 0 }}>Prompt Library</h2>
          <div className="prompt-filters">
            <PromptLibraryFilter
              value={filterOptions}
              onChange={setFilterOptions}
            />
          </div>
          <ActionButton
            label="Add Group"
            icon={<FolderAddOutlined />}
            onClick={() => setGroupModal({ ...groupModal, open: true })}
            disabled={isReadonly}
            disabledMessage={isReadonly ? getReadonlyElementTitle() : undefined}
          />
        </div>
        <Search
          placeholder="Search prompts..."
          onChange={(e) => handleSearch(e.target.value)}
          style={{ marginBottom: 24 }}
          allowClear
        />
        {loading ? (
          <div className="prompt-library-skeleton">
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ marginBottom: 18 }}>
                <Skeleton active title paragraph={{ rows: 1, width: '60%' }} />
                <Skeleton
                  active
                  paragraph={{ rows: 2 }}
                  style={{ marginTop: 8 }}
                />
              </div>
            ))}
          </div>
        ) : filteredPromptGroups.length === 0 ? (
          <div style={{ padding: '48px 0' }}>
            <Empty description="No prompts found" />
          </div>
        ) : (
          <Collapse
            // accordion
            expandIconPosition="end"
            activeKey={activeKeys}
            onChange={(keys) =>
              setActiveKeys(Array.isArray(keys) ? keys : [keys])
            }
            items={filteredPromptGroups.map((group, index) => ({
              key: index,
              label: (
                <div className="prompt-library-group-header">
                  <span className="header-logo">
                    <UserAvatar
                      userId={group.cirraUserId}
                      isSystem={group.isSystem}
                      userPicture={
                        group.cirraUserId === user?.email
                          ? user?.picture
                          : undefined
                      }
                      userName={
                        group.cirraUserId === user?.email
                          ? user?.name
                          : undefined
                      }
                      isGroup={true}
                    />
                  </span>
                  <span style={{ fontWeight: 600 }}>
                    {debouncedSearch.trim()
                      ? highlightText(group.label, debouncedSearch)
                      : group.label}
                  </span>
                  {group.isSystem && (
                    <Text type="secondary" className="system-prompt-label">
                      (System)
                    </Text>
                  )}
                </div>
              ),
              extra: (
                <>
                  <div className="group-header-actions">
                    {!group.isSystem && group.cirraUserId === user?.email && (
                      <ActionButton
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePromptGroup(group);
                        }}
                        disabled={isReadonly}
                        disabledMessage={
                          isReadonly ? getReadonlyElementTitle() : 'Delete'
                        }
                      />
                    )}
                    <ActionButton
                      icon={<PlusOutlined />}
                      size="small"
                      className="add-prompt-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddPrompt(group.id);
                      }}
                      disabled={isReadonly}
                      disabledMessage={
                        isReadonly ? getReadonlyElementTitle() : 'Add Prompt'
                      }
                    />
                  </div>
                </>
              ),
              children: (
                <List
                  dataSource={group.children}
                  renderItem={(prompt: Prompt) => (
                    <List.Item
                      actions={[
                        prompt.cirraUserId === user?.email && (
                          <ActionButton
                            icon={<EditOutlined />}
                            size="small"
                            key="edit"
                            onClick={() => handleEditPrompt(group.id, prompt)}
                            disabled={isReadonly}
                            disabledMessage={
                              isReadonly ? getReadonlyElementTitle() : 'Edit'
                            }
                          />
                        ),
                        prompt.cirraUserId === user?.email && (
                          <ActionButton
                            icon={<DeleteOutlined />}
                            size="small"
                            key="delete"
                            onClick={() => handleDeletePrompt(prompt)}
                            disabled={isReadonly}
                            disabledMessage={
                              isReadonly ? getReadonlyElementTitle() : 'Delete'
                            }
                          />
                        ),
                        <ActionButton
                          icon={<ExpandAltOutlined />}
                          size="small"
                          key="use"
                          onClick={() => handleUsePrompt(group.id, prompt)}
                          disabled={isReadonly || !!prompt.comingSoon}
                          disabledMessage={
                            isReadonly
                              ? getReadonlyElementTitle()
                              : prompt.comingSoon
                                ? PROMPT_COMING_SOON
                                : 'Use'
                          }
                        />,
                        <ActionButton
                          icon={<CopyOutlined />}
                          size="small"
                          key="copy"
                          onClick={() => handleCopyPrompt(group.id, prompt)}
                          disabled={isReadonly || !!prompt.comingSoon}
                          disabledMessage={
                            isReadonly
                              ? getReadonlyElementTitle()
                              : prompt.comingSoon
                                ? PROMPT_COMING_SOON
                                : 'Copy'
                          }
                        />,
                        <ActionButton
                          icon={<CopyPlus />}
                          size="small"
                          key="clone"
                          onClick={() => handleClonePrompt(group.id, prompt)}
                          disabled={isReadonly || !!prompt.comingSoon}
                          disabledMessage={
                            isReadonly
                              ? getReadonlyElementTitle()
                              : prompt.comingSoon
                                ? PROMPT_COMING_SOON
                                : 'Clone'
                          }
                        />,
                      ].filter(Boolean)}
                    >
                      <List.Item.Meta
                        title={
                          <div
                            className={`prompt-library-prompt-label-wrapper ${
                              prompt.isSystem ? 'system-prompt' : ''
                            }`}
                          >
                            <UserAvatar
                              userId={prompt.cirraUserId}
                              size={14}
                              isSystem={prompt.isSystem}
                              userPicture={
                                prompt.cirraUserId === user?.email
                                  ? user?.picture
                                  : undefined
                              }
                              userName={
                                prompt.cirraUserId === user?.email
                                  ? user?.name
                                  : undefined
                              }
                              isGroup={false}
                            />
                            <span>
                              {debouncedSearch.trim()
                                ? highlightText(prompt.label, debouncedSearch)
                                : prompt.label}
                              {prompt.isSystem && (
                                <Text
                                  type="secondary"
                                  className="system-prompt-label"
                                >
                                  (System)
                                </Text>
                              )}
                            </span>
                          </div>
                        }
                        description={
                          <Tooltip
                            title={
                              <div
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  textAlign: 'left',
                                }}
                              >
                                {debouncedSearch.trim()
                                  ? highlightText(prompt.value, debouncedSearch)
                                  : prompt.value}
                              </div>
                            }
                          >
                            <Text type="secondary" ellipsis>
                              {debouncedSearch.trim()
                                ? highlightText(prompt.value, debouncedSearch)
                                : prompt.value}
                            </Text>
                          </Tooltip>
                        }
                      />
                    </List.Item>
                  )}
                />
              ),
            }))}
          />
        )}
      </div>
      <Modal
        title={`${modal.mode === 'edit' ? 'Edit' : modal.mode === 'clone' ? 'Clone' : 'Add'} Prompt`}
        className="prompt-library-modal"
        open={modal.open}
        onOk={handleModalOk}
        onCancel={() => {
          setModal({
            open: false,
            groupId: '',
            prompt: null,
            mode: 'add',
            loading: false,
          });
          setNewPrompt({ label: '', value: '' });
        }}
        confirmLoading={modal.loading}
      >
        {modal.loading ? (
          <div className="linear-loading prompt-library-modal-loading"></div>
        ) : null}
        <div className="prompt-library-modal-content">
          <Input
            placeholder="Prompt Label"
            value={newPrompt.label}
            onChange={(e) =>
              setNewPrompt({ ...newPrompt, label: e.target.value })
            }
          />
          <Input.TextArea
            placeholder="Prompt Value"
            value={newPrompt.value}
            onChange={(e) =>
              setNewPrompt({ ...newPrompt, value: e.target.value })
            }
            rows={4}
          />
        </div>
      </Modal>

      <Modal
        title="Add Group"
        className="prompt-library-modal"
        open={groupModal.open}
        onOk={handleAddGroup}
        onCancel={() => {
          setGroupModal({ ...groupModal, open: false });
          setNewGroup({ label: '' });
        }}
        confirmLoading={groupModal.loading}
      >
        {groupModal.loading ? (
          <div className="linear-loading prompt-library-modal-loading"></div>
        ) : null}
        <div className="prompt-library-modal-content">
          <Input
            placeholder="Group Label"
            value={newGroup.label}
            onChange={(e) =>
              setNewGroup({ ...newGroup, label: e.target.value })
            }
          />
        </div>
      </Modal>
    </div>
  );
};

export default PromptLibrary;
